import { Header } from '@components/Header';
import { routes } from '@routes';
import { Helmet } from 'react-helmet-async';
import { AuthCallout } from '../components/AuthCallout';
import { EndpointCard } from '../components/EndpointCard';
import { EndpointSection } from '../components/EndpointSection';
import { MethodBadgeGet } from '../components/MethodBadgeGet';
import { MethodBadgePost } from '../components/MethodBadgePost';
import { ParamRow } from '../components/ParamRow';
import { ParamsTable } from '../components/ParamsTable';

export const InternalApiDocsPage = () => (
  <>
    <Helmet>
      <title>Internal Admin API Docs - AutoScript Admin</title>
    </Helmet>
    <Header title="Internal Admin API" backRoute={routes.adminApiDocs.make()} />
    <div className="mx-auto max-w-4xl px-6 py-6">
      <AuthCallout>
        Every request must send the shared <code className="font-mono text-xs">INTERNAL_API_KEY</code> in the{' '}
        <code className="font-mono text-xs">api-key</code> header. The key is managed per environment in Doppler and is
        for engineers and internal automation only: it is not scoped to an organization, so these endpoints can read and
        modify jobs across all organizations. Organization API Access Keys are not accepted.
      </AuthCallout>

      <EndpointCard
        method={<MethodBadgeGet />}
        path="/admin/internal/jobs/{jobId}"
        summary="Get job details for any organization"
      >
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="jobId" type="string" required description="Job ID; may belong to any organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="200" type="JSON object" description="The job with its current state." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/admin/internal/jobs/{jobId}/unlock"
        summary="Unlock a job without charging"
      >
        <EndpointSection title="Description">
          <p>
            Engineer-only escape hatch: sets asOneLocked to false. It does not charge the customer, does not trigger any
            pipeline event, and does not change the job status. The production pay-to-unlock flow is the billing unlock
            endpoint.
          </p>
        </EndpointSection>
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="jobId" type="string" required description="Job ID; may belong to any organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="JSON object" description="The updated job." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgeGet />}
        path="/admin/internal/jobs/{jobId}/pipeline-state"
        summary="Get pipeline diagnostic state for a job"
      >
        <EndpointSection title="Description">
          <p>
            Returns the job row, every pipeline attempt, every pipeline event including error details, job outputs, and
            pipeline timestamps. Intended for engineer debugging and automated tests. Previously exposed on the
            Organization API; now engineer-only.
          </p>
        </EndpointSection>
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="jobId" type="string" required description="Job ID; may belong to any organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow
              name="200"
              type="JSON object"
              description="Job, pipeline attempts, pipeline events, job outputs, and timestamps."
            />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgeGet />}
        path="/admin/internal/jobs/{jobId}/unlock-policy"
        summary="Re-evaluate the unlock policy for a job"
      >
        <EndpointSection title="Description">
          <p>
            Computes what the subscription-based unlock policy would return right now (FULL_UNLOCK, PARTIAL_UNLOCK, or
            LOCKED) with a human-readable reason, without changing any state. Previously exposed on the Organization
            API; now engineer-only.
          </p>
        </EndpointSection>
        <EndpointSection title="Path parameters">
          <ParamsTable>
            <ParamRow name="jobId" type="string" required description="Job ID; may belong to any organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="200" type="JSON object" description="Unlock policy verdict and reason." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/admin/internal/daily-jobs-report/fire"
        summary="Fire the daily jobs report on demand"
      >
        <EndpointSection title="Description">
          <p>
            Pulls the last 24 hours of jobs from the database, builds a CSV with per-stage timings, and uploads it to
            the Slack warnings channel. Runs the same code path as the midnight Eastern cron.
          </p>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="JSON object" description="Acknowledgement that the report was generated." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/admin/e2e/asone-load/seed-job"
        summary="Seed a synthetic ASOne load-test job"
      >
        <EndpointSection title="Description">
          <p>
            Clones a pre-uploaded fixture (audio and session text), creates a recording session and job in the load-test
            organization, and submits it to the preprocessor queue. Available in non-production environments only.
          </p>
        </EndpointSection>
        <EndpointSection title="Request body">
          <ParamsTable>
            <ParamRow name="fixtureId" type="string" required description="Fixture identifying audio and duration." />
            <ParamRow name="userEmail" type="string" description="Overrides the load-test user." />
            <ParamRow name="organizationName" type="string" description="Overrides the load-test organization." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="JSON object" description="IDs of the seeded job and recording session." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>

      <EndpointCard
        method={<MethodBadgePost />}
        path="/admin/e2e/asone-load/jobs/statuses"
        summary="Bulk job-status lookup for load-test polling"
      >
        <EndpointSection title="Description">
          <p>
            Returns status, pipeline status, and current attempt ID for the supplied job IDs. Available in
            non-production environments only.
          </p>
        </EndpointSection>
        <EndpointSection title="Request body">
          <ParamsTable>
            <ParamRow name="jobIds" type="string[]" required description="Job IDs to look up." />
          </ParamsTable>
        </EndpointSection>
        <EndpointSection title="Responses">
          <ParamsTable nameLabel="Status" typeLabel="Body">
            <ParamRow name="201" type="JSON array" description="One status entry per requested job ID." />
          </ParamsTable>
        </EndpointSection>
      </EndpointCard>
    </div>
  </>
);
